import React from 'react';

// Data structure for DQ framework results matching the new JSON API format
interface DQRuleResult {
  rule_id: string;
  rule_type: string;
  rule_description: string;
  pass_rate?: number;
  failing_record_count?: number;
  passing_record_count?: number;
  rule_configuration?: any;
}

interface DQColumnResult {
  rule_results: DQRuleResult[];
  column_pass_rate?: number;
  // Legacy support
  rules?: DQRuleResult[];
}

interface DQFrameworkResults {
  success?: boolean;
  column_results?: Record<string, DQColumnResult>;
  overall_summary?: {
    avg_pass_rate: number;
    total_records: number;
  };
}

interface UserPrescribedRulesProps {
  dqFrameworkResults?: DQFrameworkResults;
}

// Rule type labels mapping - kept for display purposes
const RULE_TYPE_LABELS: Record<string, string> = {
  'null_check': 'Null Check',
  'length_check': 'Length Validation',
  'range_check': 'Range Validation',
  'lov_check': 'List of Values',
  'invalid_char_check': 'Invalid Characters',
  'format_check': 'Format Validation',
  'pk_check': 'Primary Key',
  'data_type_check': 'Data Type',
  'err_lov_check': 'Error List Check'
};

export function UserPrescribedRules({ dqFrameworkResults }: UserPrescribedRulesProps) {
  // Handle different possible data structures from backend
  let columnResultsData: Record<string, DQColumnResult> = {};

  if (!dqFrameworkResults) {
    return null;
  }

  // Check for column_results directly
  if (dqFrameworkResults.column_results) {
    columnResultsData = dqFrameworkResults.column_results;
  }
  // Check for legacy format where rules might be in an array
  else if (Array.isArray(dqFrameworkResults)) {
    columnResultsData = dqFrameworkResults.reduce((acc, rule) => {
      if (rule.column_name) {
        if (!acc[rule.column_name]) {
          acc[rule.column_name] = { rule_results: [], rules: [] };
        }
        acc[rule.column_name].rules!.push(rule);
      }
      return acc;
    }, {} as Record<string, DQColumnResult>);
  }
  // Check if dqFrameworkResults itself contains rule data directly
  else if ((dqFrameworkResults as any).rule_type || (dqFrameworkResults as any).column_name) {
    const rule = dqFrameworkResults as any;
    if (rule.column_name) {
      columnResultsData[rule.column_name] = { rule_results: [rule], rules: [rule] };
    }
  }

  if (Object.keys(columnResultsData).length === 0) {
    return null;
  }

  // Convert column_results object to array format for easier rendering
  const columnResults = Object.entries(columnResultsData).map(([columnName, columnData]) => {
    return {
      column_name: columnName,
      ...columnData,
      rules: columnData.rule_results || [] // Use rule_results from new structure
    };
  });

  // Filter out columns with no rules for display
  const columnsWithRules = columnResults.filter(column => column.rules && column.rules.length > 0);

  // If no columns with rules, return null
  if (columnsWithRules.length === 0) {
    return null;
  }

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  const formatRuleConfiguration = (config: any, ruleType: string): string => {
    if (!config || typeof config !== 'object') {
      return 'No configuration';
    }
    const entries = Object.entries(config);
    if (entries.length === 0) {
      // Special cases for static rules - provide simple explanations
      switch (ruleType) {
        case 'null_check':
          return 'Checks that column values are not null';
        case 'pk_check':
          return 'Validates primary key uniqueness and presence';
        case 'data_type_check':
          return 'Ensures values match expected data type';
        default:
          return 'Default configuration';
      }
    }
    const configStrings = entries.slice(0, 3).map(([key, value]) => `${key}: ${value}`);
    if (entries.length > 3) {
      configStrings.push(`+${entries.length - 3} more`);
    }
    return configStrings.join(', ');
  };

  const getRuleTypeLabel = (ruleType: string): string => {
    return RULE_TYPE_LABELS[ruleType] || ruleType.replace(/_/g, ' ');
  };

  const getPassRateBackgroundColor = (passRate: number): string => {
    if (passRate >= 90) return '#10b981'; // Green
    if (passRate >= 80) return '#84cc16'; // Light green
    if (passRate >= 70) return '#eab308'; // Yellow
    if (passRate >= 60) return '#f97316'; // Orange
    if (passRate >= 40) return '#ef4444'; // Red
    return '#dc2626'; // Dark red
  };

  const getPassRateTextColor = (passRate: number): string => {
    if (passRate >= 95) return 'text-green-700'; // Dark green text
    if (passRate >= 90) return 'text-green-600'; // Green text
    if (passRate >= 80) return 'text-green-500'; // Light green text
    if (passRate >= 70) return 'text-yellow-600'; // Yellow text
    if (passRate >= 60) return 'text-orange-600'; // Orange text
    if (passRate >= 40) return 'text-red-500'; // Light red text
    return 'text-red-600'; // Red text
  };

  const getPassRateColor = (passRate: number): string => {
    if (passRate >= 90) return 'text-green-600';
    if (passRate >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPassRateBackground = (passRate: number): string => {
    if (passRate >= 90) return 'bg-green-50 border-green-200';
    if (passRate >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="w-full px-1 sm:px-2">
      <div className="w-full bg-white rounded-2xl shadow-xl p-2">
        <section>
          <div className="bg-red-50 p-6 mb-6 text-center">
            <h3
              className="text-xl font-bold mb-6"
              style={{ color: '#A4001D', fontFamily: 'Alright Sans, system-ui, sans-serif' }}
            >
              User-Prescribed Rules
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm max-w-2xl mx-auto">
              <div className="flex flex-col space-y-1">
                <span className="text-gray-600">Average Pass Rate</span>
                <div className="flex flex-col space-y-2">
                  <span className="font-bold text-red-600 text-lg">
                    {formatPercentage(
                      columnsWithRules.length > 0
                        ? columnsWithRules.reduce((sum, col) => {
                          const avgPassRate = col.rules.reduce((ruleSum, rule) => ruleSum + (rule.pass_rate || 0), 0) / (col.rules.length || 1);
                          return sum + avgPassRate;
                        }, 0) / columnsWithRules.length
                        : 0
                    )}
                  </span>
                  <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${columnsWithRules.length > 0
                          ? columnsWithRules.reduce((sum, col) => {
                            const avgPassRate = col.rules.reduce((ruleSum, rule) => ruleSum + (rule.pass_rate || 0), 0) / (col.rules.length || 1);
                            return sum + avgPassRate;
                          }, 0) / columnsWithRules.length
                          : 0
                          }%`,
                        backgroundColor: getPassRateBackgroundColor(
                          columnsWithRules.length > 0
                            ? columnsWithRules.reduce((sum, col) => {
                              const avgPassRate = col.rules.reduce((ruleSum, rule) => ruleSum + (rule.pass_rate || 0), 0) / (col.rules.length || 1);
                              return sum + avgPassRate;
                            }, 0) / columnsWithRules.length
                            : 0
                        )
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-gray-600">Total Rules</span>
                <span className="font-bold text-gray-800 text-lg">
                  {columnsWithRules.reduce((total, col) => total + col.rules.length, 0)}
                </span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-gray-600">Columns Tested</span>
                <span className="font-bold text-gray-800 text-lg">
                  {columnsWithRules.length}
                </span>
              </div>
            </div>
          </div>

          {/* Rules Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-red-50 border-b border-red-200">
                    <th className="text-left py-4 px-4 font-semibold text-red-800 text-sm w-1/6">Column</th>
                    <th className="text-left py-4 px-3 font-semibold text-red-800 text-sm w-1/6">Rule Type</th>
                    <th className="text-left py-4 px-3 font-semibold text-red-800 text-sm w-1/4">Description</th>
                    <th className="text-left py-4 px-3 font-semibold text-red-800 text-sm w-1/6">Pass Rate</th>
                    <th className="text-left py-4 px-4 font-semibold text-red-800 text-sm w-1/4">Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {columnsWithRules.map((column) => {
                    return (column.rules || []).map((rule, ruleIndex) => {
                      return (
                        <tr
                          key={`${column.column_name}-${rule.rule_type}-${ruleIndex}`}
                          className={`
                        border-b border-gray-100 transition-colors duration-150
                        ${ruleIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        hover:bg-red-50
                      `}
                        >
                          {/* Column Name */}
                          <td className="py-4 px-4">
                            {ruleIndex === 0 && (
                              <div>
                                <span className="font-medium text-gray-900 text-sm notranslate">{column.column_name}</span>
                                <div className="text-xs text-gray-500 mt-1">
                                  {(column.rules || []).length} rule{(column.rules || []).length > 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Rule Type */}
                          <td className="py-4 px-3">
                            <span className="inline-block bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
                              {getRuleTypeLabel(rule.rule_type)}
                            </span>
                          </td>

                          {/* Rule Description */}
                          <td className="py-4 px-3">
                            <div className="text-gray-700 text-sm leading-relaxed">
                              {rule.rule_description || `${getRuleTypeLabel(rule.rule_type)} validation for ${column.column_name}`}
                            </div>
                          </td>

                          {/* Pass Rate */}
                          <td className="py-4 px-3">
                            {rule.pass_rate !== undefined && (
                              <div className="relative group">
                                <div className="w-20 h-4 bg-gray-200 rounded-full overflow-hidden cursor-pointer relative">
                                  <div
                                    className="h-full rounded-full transition-all duration-700 ease-out"
                                    style={{
                                      width: `${rule.pass_rate}%`,
                                      backgroundColor: getPassRateBackgroundColor(rule.pass_rate)
                                    }}
                                  />
                                  {/* Pass rate percentage text on progress bar */}
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-xs font-medium text-gray-700">
                                      {formatPercentage(rule.pass_rate)}
                                    </span>
                                  </div>
                                </div>
                                {/* Tooltip showing actual counts on hover */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg">
                                  <div className="text-center">
                                    <div className="text-green-400">✓ Passed: {rule.passing_record_count !== undefined ? formatNumber(rule.passing_record_count) : 'N/A'}</div>
                                    <div className="text-red-400">✗ Failed: {rule.failing_record_count !== undefined ? formatNumber(rule.failing_record_count) : 'N/A'}</div>
                                  </div>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Rule Configuration */}
                          <td className="py-4 px-4">
                            <div className="text-gray-700 text-sm break-words max-w-xs">
                              {formatRuleConfiguration(rule.rule_configuration, rule.rule_type)}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}